import { Client, Installment, Order, Plan, Role } from 'models'
import { checkRoles, isNil, omitBy } from 'utils'
import { create as baseCreate, post } from 'services/billing'
import { ReadResult } from 'interfaces/queryOptions'
import { Coupon } from 'models/coupon'

export interface Cart {
  order: Partial<Order>
  plan: ReadResult<Plan>
  couponApplied?: Partial<Coupon>
  orderInstallments?: Installment[]
}

type OrderPayload = {
  attributes: Partial<Order>
  relationships: {
    client: {
      type: 'client'
      id: number
    }
    plan: {
      type: 'plan'
      id: number
    }
    coupon?: {
      type: 'coupon'
      id: number
    }
    installments?: {
      type: 'order_installments'
      attributes: Installment
    }[]
  }
}

export async function create(
  cart: Cart[],
  client: ReadResult<Client>,
  staffId?: string
): Promise<void> {
  await checkRoles([Role.MANAGER, Role.SUPPORT, Role.SALES])

  const payload: OrderPayload[] = []

  for (const item of cart) {
    const entry: OrderPayload = {
      attributes: { ...item.order, staff_id: staffId },
      relationships: {
        client: { type: 'client', id: client.id },
        plan: { type: 'plan', id: item.plan.id }
      }
    }

    if (item.couponApplied) {
      entry.relationships.coupon = {
        type: 'coupon',
        id: item.couponApplied?.id as number
      }
    }

    if (item.orderInstallments && item.orderInstallments.length > 0) {
      entry.relationships.installments = item.orderInstallments.map(
        (installment) => ({
          type: 'order_installments',
          attributes: {
            type: installment.type,
            amount: installment.amount,
            due_at: installment.due_at,
            upfront: installment.upfront,
            eligible: installment.eligible
          }
        })
      )
    }

    if (entry.attributes.fine_amount === 0) {
      entry.attributes.fine_mode = 'fixed'
    }

    entry.attributes = {
      ...omitBy(entry.attributes, isNil)
    }

    payload.push(entry)
  }

  await baseCreate<Order>('/orders', payload)
}

export async function addItems(
  orderId: number,
  itemIds: number[]
): Promise<void> {
  await checkRoles([Role.MANAGER, Role.LOGISTICS])
  await post(`/orders/${orderId}/items`, { item_ids: itemIds })
}
